import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface BankaKredi {
    bankaKrediID: number;
    bankaAdi?: string;
    subeAdi?: string;
    telefon?: string;
    ilgiliKisi?: string;
    bankaLogo?: string;
    vadeOrani?: number;
}

interface BankaKrediForm {
    bankaAdi: string;
    subeAdi: string;
    telefon: string;
    ilgiliKisi: string;
    vadeOrani: string;
}

const BankaKredileri: React.FC = () => {
    const [bankaKredileri, setBankaKredileri] = useState<BankaKredi[]>([]);
    const [selectedBanka, setSelectedBanka] = useState<BankaKredi | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');
    const [formData, setFormData] = useState<BankaKrediForm>({
        bankaAdi: '',
        subeAdi: '',
        telefon: '',
        ilgiliKisi: '',
        vadeOrani: ''
    });

    const fetchBankaKredileri = async () => {
        try {
            const response = await axios.get('/api/BankaKredileri');
            setBankaKredileri(response.data);
        } catch {
            toast.error('Banka kredileri yüklenirken bir hata oluştu');
        }
    };

    useEffect(() => { fetchBankaKredileri(); }, []);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('bankaAdi', formData.bankaAdi);
            formDataToSend.append('subeAdi', formData.subeAdi);
            formDataToSend.append('telefon', formData.telefon);
            formDataToSend.append('ilgiliKisi', formData.ilgiliKisi);
            formDataToSend.append('vadeOrani', formData.vadeOrani.replace(',', '.'));
            
            if (logoFile) {
                formDataToSend.append('logoFile', logoFile);
            }

            if (selectedBanka) {
                await axios.put(`/api/BankaKredileri/${selectedBanka.bankaKrediID}`, formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banka kredisi güncellendi');
            } else {
                await axios.post('/api/BankaKredileri', formDataToSend, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banka kredisi eklendi');
            }
            fetchBankaKredileri();
            handleCloseModal();
        } catch {
            toast.error('İşlem sırasında bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (banka: BankaKredi) => {
        setSelectedBanka(banka);
        setFormData({
            bankaAdi: banka.bankaAdi || '',
            subeAdi: banka.subeAdi || '',
            telefon: banka.telefon || '',
            ilgiliKisi: banka.ilgiliKisi || '',
            vadeOrani: banka.vadeOrani?.toString() || ''
        });
        if (banka.bankaLogo) {
            setLogoPreview(`${axios.defaults.baseURL}/logos/${banka.bankaLogo}`);
        }
        setShowModal(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu banka kredisini silmek istediğinize emin misiniz?')) return;
        try {
            await axios.delete(`/api/BankaKredileri/${id}`);
            toast.success('Banka kredisi silindi');
            fetchBankaKredileri();
        } catch {
            toast.error('Silme işlemi sırasında bir hata oluştu');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBanka(null);
        setLogoFile(null);
        setLogoPreview('');
        setFormData({
            bankaAdi: '',
            subeAdi: '',
            telefon: '',
            ilgiliKisi: '',
            vadeOrani: ''
        });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .bk-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                .bk-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .bk-header-left { display: flex; flex-direction: column; gap: 3px; }
                .bk-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .bk-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .bk-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .bk-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transform: translateY(-1px);
                }
                .bk-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                .bk-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                .bk-table { width: 100%; border-collapse: collapse; }
                .bk-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .bk-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .bk-table thead th.bk-th-actions { text-align: right; }
                .bk-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .bk-table tbody tr:last-child { border-bottom: none; }
                .bk-table tbody tr.bk-row-hovered { background: #f0f5ff; }
                .bk-table tbody td {
                    padding: 15px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                .bk-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 56px; }
                .bk-cell-logo {
                    width: 80px;
                }
                .bk-logo-img {
                    width: 50px;
                    height: 50px;
                    object-fit: contain;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    background: #f8fafc;
                }
                .bk-cell-name { font-weight: 600; color: #0f172a; font-size: 14px; }
                .bk-cell-actions { width: 90px; text-align: right; }

                .bk-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .bk-row-hovered .bk-actions { opacity: 1; }
                .bk-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .bk-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .bk-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .bk-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                .bk-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .bk-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .bk-count span { color: #475569; font-weight: 600; }

                .bk-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .bk-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .bk-empty-text { font-size: 15px; font-weight: 500; }

                .bk
                .bk-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: bkDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                    max-height: 90vh;
                    display: flex;
                    flex-direction: column;
                }
                @keyframes bkDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .bk-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .bk-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .bk-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .bk-modal-close:hover { background: #f3f4f6; color: #374151; }
                .bk-modal-body { 
                    padding: 24px; 
                    display: flex; 
                    flex-direction: column; 
                    gap: 20px;
                    overflow-y: auto;
                }
                .bk-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: #64748b; margin-bottom: 6px;
                }
                .bk-field input[type="text"],
                .bk-field input[type="number"] {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .bk-field input:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .bk-field-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }
                .bk-logo-upload {
                    border: 2px dashed #e2e8f0;
                    border-radius: 10px;
                    padding: 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                }
                .bk-logo-upload:hover {
                    border-color: #cd6906;
                    background: #fff8f0;
                }
                .bk-logo-upload input {
                    display: none;
                }
                .bk-logo-preview {
                    width: 100px;
                    height: 100px;
                    object-fit: contain;
                    margin: 10px auto;
                    display: block;
                    border-radius: 8px;
                }
                .bk-upload-text {
                    font-size: 13px;
                    color: #64748b;
                    margin-top: 8px;
                }
                .bk-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .bk-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .bk-btn-cancel:hover { background: #f9fafb; }
                .bk-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .bk-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .bk-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .bk-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: bkSpin 0.7s linear infinite;
                }
                @keyframes bkSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Banka Kredileri</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Banka kredi kayıtlarını yönetin</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Banka Kredisi
                    </button>
                </div>

                {/* Card List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {bankaKredileri.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af', fontSize: 14 }}>
                            <div style={{ fontSize: 32, marginBottom: 10 }}>🏦</div>
                            <div>Henüz banka kredisi eklenmedi</div>
                        </div>
                    ) : (
                        bankaKredileri.map((banka) => (
                            <div
                                key={banka.bankaKrediID}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid #f3f4f6', background: '#fff', transition: 'all 0.15s', cursor: 'default' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fef9f4'; e.currentTarget.style.borderColor = '#fde3c1'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(205,105,6,0.08)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                                    <div style={{ width: 44, height: 44, borderRadius: 9, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                        {banka.bankaLogo ? (
                                            <img src={`${axios.defaults.baseURL}/logos/${banka.bankaLogo}`} alt={banka.bankaAdi || 'Logo'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            <span style={{ fontSize: 22 }}>🏦</span>
                                        )}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{banka.bankaAdi || '—'}</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                            {[banka.subeAdi, banka.telefon, banka.ilgiliKisi].filter(Boolean).join(' • ') || `#${banka.bankaKrediID}`}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    {banka.vadeOrani != null && (
                                        <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#fef3e0', color: '#cd6906' }}>%{banka.vadeOrani}</span>
                                    )}
                                    <button
                                        title="Düzenle"
                                        onClick={() => handleEdit(banka)}
                                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                    >
                                        <svg style={{ width: 14, height: 14, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    </button>
                                    <button
                                        title="Sil"
                                        onClick={() => handleDelete(banka.bankaKrediID)}
                                        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #fee2e2', background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                    >
                                        <svg style={{ width: 14, height: 14, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                    {bankaKredileri.length > 0 && (
                        <div style={{ padding: '10px 4px', borderTop: '1px solid #f3f4f6', color: '#6b7280', fontSize: 12 }}>
                            Toplam <b style={{ color: '#111827' }}>{bankaKredileri.length}</b> banka kredisi
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="bk-overlay" onClick={handleCloseModal}>
                    <div className="bk-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedBanka ? 'Banka Kredisi Düzenle' : 'Yeni Banka Kredisi Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="bk-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="bankaAdiInput">Banka Adı</label>
                                    <input
                                        type="text"
                                        id="bankaAdiInput"
                                        placeholder="Örn: Ziraat Bankası"
                                        value={formData.bankaAdi}
                                        onChange={e => setFormData({ ...formData, bankaAdi: e.target.value })}
                                        maxLength={200}
                                        autoFocus
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="subeAdiInput">Şube Adı</label>
                                        <input
                                            type="text"
                                            id="subeAdiInput"
                                            placeholder="Örn: Merkez Şube"
                                            value={formData.subeAdi}
                                            onChange={e => setFormData({ ...formData, subeAdi: e.target.value })}
                                            maxLength={200}
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="telefonInput">Telefon</label>
                                        <input
                                            type="text"
                                            id="telefonInput"
                                            placeholder="05XX XXX XX XX"
                                            value={formData.telefon}
                                            onChange={e => setFormData({ ...formData, telefon: e.target.value })}
                                            maxLength={50}
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="ilgiliKisiInput">İlgili Kişi</label>
                                        <input
                                            type="text"
                                            id="ilgiliKisiInput"
                                            placeholder="Ad Soyad"
                                            value={formData.ilgiliKisi}
                                            onChange={e => setFormData({ ...formData, ilgiliKisi: e.target.value })}
                                            maxLength={200}
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }} htmlFor="vadeOraniInput">Vade Oranı (%)</label>
                                        <input
                                            type="text"
                                            id="vadeOraniInput"
                                            placeholder="Örn: 2,49"
                                            value={formData.vadeOrani}
                                            onChange={e => {
                                                const val = e.target.value.replace(/[^0-9.,]/g, '');
                                                setFormData({ ...formData, vadeOrani: val });
                                            }}
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Banka Logosu</label>
                                    <label className="bk-logo-upload">
                                        <input 
                                            type="file" 
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                        />
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="Logo Önizleme" className="bk-logo-preview" />
                                        ) : (
                                            <div>
                                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏦</div>
                                                <div className="bk-upload-text">Logo yüklemek için tıklayın</div>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >İptal</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving ? '#e5b678' : '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: saving ? 'none' : '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                                    onMouseLeave={e => { if (!saving) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                                >
                                    {saving && <span className="bk-spinner" />}
                                    {selectedBanka ? 'Güncelle' : 'Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default BankaKredileri;
